import React from "react"
import { IconTriangleWarningFill18 } from "nucleo-ui-fill-18"

import { cn } from "@productlane/lib/src/cn"

import { LandingCard } from "./landing-card"
import { Tooltip } from "./tooltip"

type IntegrationCardVariant = "page" | "grid" | "compact"

interface Props {
	variant?: IntegrationCardVariant
	name: string | React.ReactNode
	logo: React.ReactNode
	/** The logo is a full-bleed tile, so the box drops its padding and clips it. */
	logoFill?: boolean
	description?: string
	instructions?: React.ReactNode
	button?: React.ReactElement | null
	notice?: React.ReactNode
	image?: string
	alt?: string
	connected?: boolean
	warning?: string
	onClick?: () => void
	className?: string
}

function LogoBox({
	children,
	className,
}: {
	children: React.ReactNode
	className?: string
}) {
	return (
		<div
			className={cn(
				"inline-flex flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white shadow-md shadow-black/[0.03] dark:border-gray-600 dark:bg-gray-700/70 [&_svg]:flex-none",
				className,
			)}
		>
			{children}
		</div>
	)
}

function WarningMark({ label }: { label: string }) {
	return (
		<Tooltip label={label}>
			<span aria-label={label} className="flex flex-none">
				<IconTriangleWarningFill18 className="h-3.5 w-3.5 flex-none text-yellow-500" />
			</span>
		</Tooltip>
	)
}

function IntegrationCard({
	variant = "page",
	name,
	logo,
	logoFill,
	description,
	instructions,
	button,
	notice,
	image,
	alt,
	connected,
	warning,
	onClick,
	className,
}: Props) {
	if (variant === "grid") {
		return (
			<button
				type="button"
				onClick={onClick}
				className={cn(
					"flex h-full min-h-[148px] cursor-default flex-col rounded-xl bg-white p-4 text-left shadow-card transition-colors hover:bg-gray-150 dark:bg-gray-850 dark:shadow-card-dark dark:hover:bg-gray-800",
					className,
				)}
			>
				<div className="flex items-center gap-3">
					<LogoBox
						className={cn(
							"h-9 w-9 rounded-lg shadow-none",
							logoFill
								? "overflow-hidden border-0 p-0 [&_svg]:h-full [&_svg]:w-full"
								: "p-[7px] [&_svg]:h-5 [&_svg]:w-5",
						)}
					>
						{logo}
					</LogoBox>
					<div className="min-w-0 flex-1 text-[15px] leading-5">
						<span className="flex min-w-0 items-center gap-1.5 font-medium text-primary">
							<span className="truncate">{name}</span>
							{warning && <WarningMark label={warning} />}
						</span>
						{connected && <div className="text-secondary">Enabled</div>}
					</div>
				</div>
				{description && (
					<p className="mt-4 line-clamp-3 text-sm leading-5 text-secondary">
						{description}
					</p>
				)}
			</button>
		)
	}

	if (variant === "compact") {
		return (
			<button
				type="button"
				onClick={onClick}
				className={cn(
					"flex cursor-default items-center gap-3 rounded-[10px] bg-white p-3 text-left shadow-card transition-colors hover:bg-gray-150 dark:bg-gray-850 dark:shadow-card-dark dark:hover:bg-gray-800",
					className,
				)}
			>
				<LogoBox className="h-9 w-9 p-1.5">{logo}</LogoBox>
				<div className="min-w-0 flex-1">
					<span className="flex min-w-0 items-center gap-1.5 text-sm font-medium leading-5 text-primary">
						<span className="truncate">{name}</span>
						{warning && <WarningMark label={warning} />}
					</span>
					{connected && (
						<div className="text-[13px] leading-[18px] text-tertiary">
							Enabled
						</div>
					)}
				</div>
			</button>
		)
	}

	return (
		<main className="mx-auto w-full max-w-3xl pb-20">
			<div className="mx-auto flex flex-col gap-12">
				<div className="flex flex-col gap-4">
					<section className="">
						<LandingCard className="-mx-3.5 p-5">
							<div className="flex flex-row items-center justify-between gap-4">
								<div className="flex items-center gap-4">
									<LogoBox className="h-16 w-16 p-3">{logo}</LogoBox>
									<div className="">
										<div className="mb-1 text-lg font-semibold text-primary">
											{name}
										</div>
										<div className="text-sm leading-5 tracking-tight text-secondary">
											{description}
										</div>
									</div>
								</div>

								{button && <div className="w-full md:w-auto">{button}</div>}
							</div>
							{notice && <div className="mt-5">{notice}</div>}
							{image && (
								<div className="mt-8 overflow-hidden rounded-2xl">
									<img src={image} alt={alt} />
								</div>
							)}
						</LandingCard>
					</section>
				</div>

				<div className="lg:flex">
					{instructions && <div className="w-full">{instructions}</div>}
				</div>
			</div>
		</main>
	)
}

export { IntegrationCard }
export type { IntegrationCardVariant }
